import { ReactElement } from 'react'
import { useNavigate } from 'react-router-dom'
import { Badge } from 'app/components/ui/badge'
import { Button } from 'app/components/ui/button'
import { Input } from 'app/components/ui/input'
import { Textarea } from 'app/components/ui/textarea'
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetClose } from 'app/components/ui/sheet'
import { cn } from 'app/lib/utils'
import { formatDate, formatDateTime, type Prompt, type PromptVersion } from './mockData'
import { PromptDiff } from './PromptDiff'
import { usePromptsPanel } from './usePromptsPanel'

// The shared Input strips its focus ring; opt these fields back in to match the
// prompt-body Textarea's blue focus outline.
const FOCUS_RING = `focus:ring-1 focus:ring-blue-500`

function PromptListItem({
  prompt,
  active,
  onSelect,
}: {
  prompt: Prompt
  active: boolean
  onSelect: (id: string) => void
}): ReactElement {
  return (
    <button
      type="button"
      onClick={() => onSelect(prompt.id)}
      className={cn(
        `w-full rounded-lg border p-3 text-left transition-colors`,
        active ? `border-blue-500 bg-blue-50` : `border-default bg-surface-raised hover:bg-surface-subtle`,
      )}
    >
      <span className="block truncate text-body-sm font-medium text-strong">{prompt.name}</span>
      <div className="mt-1.5 flex flex-wrap items-center gap-1.5 text-caption-sm text-subtle">
        Updated {formatDate(prompt.updatedAt)}
        {prompt.underEvaluation && <Badge color="blue" label="not live" />}
      </div>
    </button>
  )
}

function VersionRow({
  version,
  isActive,
  isDraft,
  onView,
}: {
  version: PromptVersion
  isActive: boolean
  isDraft: boolean
  onView: (version: PromptVersion) => void
}): ReactElement {
  return (
    <button
      type="button"
      onClick={() => onView(version)}
      className="w-full rounded-lg border border-default bg-surface-raised p-3 text-left transition-colors hover:bg-surface-subtle"
    >
      <div className="flex items-center gap-1.5">
        <span className="text-body-sm font-medium text-strong">v{version.version}</span>
        {isActive && <Badge color="green" label="active" />}
        {isDraft && <Badge color="amber" label="draft" />}
      </div>
      <p className="mt-1 line-clamp-2 text-caption-sm text-subtle">{version.changeSummary}</p>
      <p className="mt-1 text-caption-sm text-subtle">
        {version.editor} · {formatDate(version.savedAt)}
      </p>
    </button>
  )
}

export function PromptsPanelComponent(): ReactElement {
  const {
    prompts,
    selected,
    selectedId,
    draft,
    isDirty,
    isDraftLive,
    lastSavedAt,
    versions,
    viewing,
    viewingPrevBody,
    onSelect,
    onDraftChange,
    onSave,
    onActivate,
    onReset,
    onViewVersion,
    onCloseView,
  } = usePromptsPanel()
  const navigate = useNavigate()

  // Three columns need ~1200px of content, so the layout steps down: stacked below `lg`
  // (stages as a card grid, then editor, then history), stage list + editor from `lg` with
  // the history under the editor, and the original three columns from `2xl`.
  return (
    <div className="grid gap-6 lg:grid-cols-[13rem_minmax(0,1fr)] 2xl:grid-cols-[15rem_minmax(0,1fr)_20rem]">
      {/* Column 1 — stage list */}
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:flex lg:flex-col">
        {prompts.length === 0 ? (
          <p className="px-1 py-4 text-caption-sm text-subtle">No prompts.</p>
        ) : (
          prompts.map((prompt) => (
            <PromptListItem key={prompt.id} prompt={prompt} active={prompt.id === selectedId} onSelect={onSelect} />
          ))
        )}
      </div>

      {/* Column 2 — editor */}
      <div className="min-w-0 flex-1">
        {!selected || !draft ? (
          <div className="rounded-xl border border-dashed border-default bg-surface-raised p-10">
            <p className="text-body-sm text-subtle">Select a stage prompt to view or edit it.</p>
          </div>
        ) : (
          // A container, because the editor's width depends on the column layout and the
          // sidebar, not only the viewport: the actions sit beside the title only when the
          // card itself has room for both.
          <div className="@container rounded-xl border border-default bg-surface-raised p-4 sm:p-6">
            <div className="flex flex-col gap-3 @min-[40rem]:flex-row @min-[40rem]:items-start @min-[40rem]:justify-between @min-[40rem]:gap-4">
              <div className="min-w-0">
                <h4 className="text-strong">{selected.name}</h4>
                <p className="mt-1 text-caption-sm text-subtle">
                  <span className="font-mono text-subtle">{selected.id}</span> · Updated{` `}
                  {formatDateTime(selected.updatedAt)}
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-2 @min-[40rem]:shrink-0 @min-[40rem]:flex-nowrap">
                <Button
                  variant="white"
                  size="sm"
                  onClick={() =>
                    navigate(
                      `/ops/evals?new=1&dataset=ds_call_processing_golden&stage=${selected.key}&version=${selected.draftVersion}`,
                    )
                  }
                >
                  Evaluate Draft
                </Button>
                <Button variant="white" size="sm" onClick={onReset} disabled={!isDirty}>
                  Reset
                </Button>
                {!selected.underEvaluation && (
                  <Button variant="secondary" size="sm" onClick={onActivate} disabled={isDraftLive}>
                    Activate Version
                  </Button>
                )}
                <Button variant="primary" size="sm" onClick={onSave} disabled={!isDirty}>
                  Save Draft
                </Button>
              </div>
            </div>

            {selected.underEvaluation ? (
              <div className="mt-4 rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-caption-sm text-blue-900">
                Production does not run this prompt. It is the v2 pipeline under evaluation: one call instead of five,
                followed by the same stage 5 rules. Use <span className="font-medium">Evaluate Draft</span> to compare
                it with the 5-stage pipeline.
              </div>
            ) : (
              !isDraftLive && (
                <div className="mt-4 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-caption-sm text-amber-900">
                  Draft v{selected.draftVersion} is not live yet — the pipeline still uses active v
                  {selected.activeVersion}. Click <span className="font-medium">Activate Version</span> to make it live.
                </div>
              )
            )}

            {lastSavedAt && !isDirty && (
              <div className="mt-4 rounded-md border border-green-200 bg-green-50 px-3 py-2 text-caption-sm text-green-800">
                Draft saved locally at {formatDateTime(lastSavedAt)}. Changes are not persisted (frontend-only).
              </div>
            )}

            <div className="mt-6 grid gap-5">
              <div className="grid gap-1.5">
                <label className="text-caption-sm font-medium text-default">Name</label>
                <Input
                  value={draft.name}
                  onChange={(event) => onDraftChange({ name: event.target.value })}
                  className={FOCUS_RING}
                />
              </div>

              <div className="grid gap-1.5">
                <label className="text-caption-sm font-medium text-default">Description</label>
                <Input
                  value={draft.description}
                  onChange={(event) => onDraftChange({ description: event.target.value })}
                  className={FOCUS_RING}
                />
              </div>

              <div className="grid gap-1.5">
                <label className="text-caption-sm font-medium text-default">Prompt body</label>
                <Textarea
                  value={draft.body}
                  onChange={(event) => onDraftChange({ body: event.target.value })}
                  className="min-h-[260px] bg-surface-raised font-mono text-body-xs leading-relaxed"
                />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Column 3 — version history for the selected prompt (under the editor below 2xl) */}
      <section className="flex flex-col gap-2 lg:col-start-2 2xl:col-start-auto">
        <h5 className="px-1 text-caption-sm font-medium text-subtle">Version history</h5>
        {selected && versions.length > 0 ? (
          <div className="grid gap-3 sm:grid-cols-2 2xl:flex 2xl:flex-col">
            {versions.map((version) => (
              <VersionRow
                key={version.id}
                version={version}
                isActive={version.version === selected.activeVersion}
                isDraft={version.version === selected.draftVersion}
                onView={onViewVersion}
              />
            ))}
          </div>
        ) : (
          <p className="px-1 text-caption-sm text-subtle">No versions yet.</p>
        )}
      </section>

      <Sheet open={viewing != null} onOpenChange={(open) => !open && onCloseView()}>
        <SheetContent className="p-6">
          <SheetHeader className="p-0">
            <SheetTitle>
              {viewing?.promptName} · v{viewing?.version}
            </SheetTitle>
            <SheetDescription>
              {viewing && `Saved ${formatDateTime(viewing.savedAt)} by ${viewing.editor}`}
            </SheetDescription>
          </SheetHeader>
          <div className="mt-4 flex flex-col gap-4">
            <div>
              <span className="text-caption-sm font-medium text-default">Change summary</span>
              <p className="mt-1 text-body-sm text-subtle">{viewing?.changeSummary}</p>
            </div>
            <div>
              <span className="text-caption-sm font-medium text-default">Prompt body</span>
              <pre className="mt-1 whitespace-pre-wrap rounded-md border border-default bg-surface-raised p-3 font-mono text-body-xs leading-relaxed text-default">
                {viewing?.body}
              </pre>
            </div>
            {viewing && viewingPrevBody != null && (
              <div>
                <span className="text-caption-sm font-medium text-default">Changes from previous version</span>
                <div className="mt-1">
                  <PromptDiff oldBody={viewingPrevBody} newBody={viewing.body} />
                </div>
              </div>
            )}
            <div className="flex justify-end">
              <SheetClose asChild>
                <Button variant="white" size="sm">
                  Close
                </Button>
              </SheetClose>
            </div>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  )
}
